import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'shared/icons';
import PageHeader from '../components/layout/PageHeader';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import StandardTable from '../components/ui/StandardTable';
import { useToast } from '../components/ui/Toast';
import UserMcpPanel from '../components/UserMcpPanel';
import { useAuth } from '../hooks/useAuth';
import api from '../services/api';
import { FormatDateTimeBr } from '../utils/date';

const DEFAULT_LIMIT = 5;

const formatDateTime = (value) => FormatDateTimeBr(value, { blank: '-' });

function parseUserAgent(ua) {
    const value = String(ua || '').toLowerCase();
    if (!value) {
        return 'Desconhecido';
    }
    if (value.includes('chrome')) {
        return 'Chrome';
    }
    if (value.includes('firefox')) {
        return 'Firefox';
    }
    if (value.includes('safari')) {
        return 'Safari';
    }
    if (value.includes('edge')) {
        return 'Edge';
    }
    return 'Outro';
}

const UserProfilePage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const { checkPermission } = useAuth();
    const canManageUsers = checkPermission('users:write');

    const [loadingUser, setLoadingUser] = useState(true);
    const [loadingHistory, setLoadingHistory] = useState(true);
    const [user, setUser] = useState(null);
    const [historyRows, setHistoryRows] = useState([]);
    const [historyPage, setHistoryPage] = useState(1);
    const [historyPageSize, setHistoryPageSize] = useState(DEFAULT_LIMIT);
    const [historyPaging, setHistoryPaging] = useState({ total: 0, limit: DEFAULT_LIMIT, offset: 0 });

    useEffect(() => {
        if (!id) {
            return;
        }

        let active = true;
        setLoadingUser(true);
        api.getUser(id)
            .then((data) => {
                if (active) {
                    setUser(data || null);
                }
            })
            .catch((error) => {
                if (!active) {
                    return;
                }
                toast.error(error.message || 'Erro ao carregar usuario');
                setUser(null);
            })
            .finally(() => {
                if (active) {
                    setLoadingUser(false);
                }
            });

        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        if (!id) {
            return;
        }

        const offset = (historyPage - 1) * historyPageSize;
        let active = true;
        setLoadingHistory(true);
        api.getUserLoginHistory(id, {
            limit: historyPageSize,
            offset,
        })
            .then((data) => {
                if (!active) {
                    return;
                }
                const rows = Array.isArray(data?.results) ? data.results : [];
                setHistoryRows(rows);
                setHistoryPaging({
                    total: Number(data?.paging?.total || 0),
                    limit: Number(data?.paging?.limit || historyPageSize),
                    offset: Number(data?.paging?.offset || offset),
                });
            })
            .catch((error) => {
                if (!active) {
                    return;
                }
                toast.error(error.message || 'Erro ao carregar historico do usuario');
                setHistoryRows([]);
                setHistoryPaging({
                    total: 0,
                    limit: historyPageSize,
                    offset,
                });
            })
            .finally(() => {
                if (active) {
                    setLoadingHistory(false);
                }
            });

        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, historyPage, historyPageSize]);

    const loginHistoryColumns = useMemo(
        () => [
            {
                key: 'login_at',
                label: 'Data/Hora',
                getValue: (row) => formatDateTime(row?.login_at),
            },
            {
                key: 'ip_address',
                label: 'IP',
                getValue: (row) => row?.ip_address || '-',
                cellClassName: 'font-mono text-black dark:text-white',
            },
            {
                key: 'user_agent',
                label: 'Navegador',
                getValue: (row) => parseUserAgent(row?.user_agent),
            },
        ],
        [],
    );

    return (
        <div className="space-y-6">
            <PageHeader
                title="Perfil do usuario"
                subtitle={user?.id ? `Usuario ${user.id}` : 'Consulta de usuario'}
                actions={
                    <Button variant="secondary" size="sm" onClick={() => navigate('/users')}>
                        <ArrowLeft size={16} className="mr-2" />
                        Voltar
                    </Button>
                }
            />

            {loadingUser ? (
                <Card>
                    <p className="text-sm text-mono-500 dark:text-mono-400">Carregando usuario...</p>
                </Card>
            ) : !user ? (
                <Card>
                    <p className="text-sm text-mono-500 dark:text-mono-400">Usuario nao encontrado.</p>
                </Card>
            ) : (
                <>
                    <Card title="Informacoes gerais">
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                            <div>
                                <p className="text-xs text-mono-500 dark:text-mono-400">Nome</p>
                                <p className="text-sm font-semibold text-black dark:text-white">
                                    {user.full_name || '-'}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-mono-500 dark:text-mono-400">E-mail</p>
                                <p className="text-sm text-black dark:text-white">{user.email || '-'}</p>
                            </div>
                            <div>
                                <p className="text-xs text-mono-500 dark:text-mono-400">Organizacao</p>
                                <p className="text-sm text-black dark:text-white">
                                    {user.organization_name || user.organization_id || '-'}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-mono-500 dark:text-mono-400">Ultimo login</p>
                                <p className="text-sm text-black dark:text-white">{formatDateTime(user.last_login)}</p>
                            </div>
                            <div>
                                <p className="text-xs text-mono-500 dark:text-mono-400">Ultimo IP</p>
                                <p className="text-sm font-mono text-black dark:text-white">{user.last_ip || '-'}</p>
                            </div>
                            <div>
                                <p className="text-xs text-mono-500 dark:text-mono-400">Status</p>
                                <p className="text-sm text-black dark:text-white">
                                    {user.is_active ? 'Ativo' : 'Inativo'}
                                </p>
                            </div>
                        </div>
                    </Card>

                    <Card title="Permissoes">
                        <div className="flex flex-wrap gap-2">
                            {(Array.isArray(user.permissions) ? user.permissions : []).length > 0 ? (
                                (user.permissions || []).map((permission) => (
                                    <Badge key={permission}>{permission}</Badge>
                                ))
                            ) : (
                                <p className="text-sm text-mono-500 dark:text-mono-400">Usuario sem permissoes.</p>
                            )}
                        </div>
                    </Card>

                    <Card title="Historico de acessos">
                        <StandardTable
                            rows={loadingHistory ? [] : historyRows}
                            columns={loginHistoryColumns}
                            rowKey={(row, index) => row?.id || `${row?.login_at || 'row'}-${index}`}
                            searchEnabled={false}
                            exportEnabled={false}
                            paginationMode="server"
                            totalItems={historyPaging.total}
                            currentPage={historyPage}
                            onPageChange={setHistoryPage}
                            itemsPerPage={historyPageSize}
                            onItemsPerPageChange={(next) => {
                                setHistoryPageSize(next);
                                setHistoryPage(1);
                            }}
                            pageSizeOptions={[5, 15, 30, 50, 100, 200]}
                            emptyMessage={loadingHistory ? 'Carregando historico...' : 'Sem historico de acesso.'}
                            countLabel="acessos"
                        />
                    </Card>

                    {/* MCP: conexões, histórico e revogação — antes um modal na
                        lista de usuários, agora dentro da própria página (26/08). */}
                    <UserMcpPanel user={user} canRevoke={canManageUsers} />
                </>
            )}
        </div>
    );
};

export default UserProfilePage;
